import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material";
import { QuestionsIF } from "../../QuestionsIF";
import { NumericQuestionLayout } from "./numericQuestionLayout";
import { ReactComponent as BloodPressureIcon } from "../../../../assets/numericInputIcons/bloodPressure.svg";
import { NumericInput } from "./numericInput";
import { QualifiedFeature } from "fe-common/graphql";
import { NumericFeatureIds } from "../rangeIds";
import { PatientColorMap } from "../../../../layout/patientColorMap";
import { onRtl } from "fe-web-components/rtl/rtlStyles";
import { useTranslate } from "../../../../hooks/useTranslate";
import clsx from "clsx";
//region [[ Styles ]]

const BloodPressureQuestionView = styled(NumericQuestionLayout)({});

const BloodPressureContainer = styled("div")({
  display: "flex",
  flexFlow: "row",
  alignItems: "center",
  width: "100%",
  flexWrap: "wrap",
  justifyContent: "center",
  "& svg": {
    flexBasis: "36px",
    "& [fill='#5A52C3']": {
      fill: PatientColorMap.Primary,
    },
  },
  position: "relative",
  "&.mini-view": {
    justifyContent: "flex-start",
    padding: "0 12px",
    "& .input-section": {
      flex: 1,
      "& .numeric-input-view": {
        fontSize: "10px",
      },
    },
  },
  ...onRtl({
    flexFlow: "row-reverse",
  }),
});

const MiniViewText = styled("div")({
  fontSize: "12px",
  fontWeight: 500,
});

const InputsSection = styled("div")({
  display: "flex",
  flexFlow: "row",
  justifyContent: "center",
  alignItems: "flex-start",
  flexBasis: "calc(80% - 40px)",
  margin: "5px 15px",
  ...onRtl({
    flexFlow: "row-reverse",
  }),
});

const BloodPressureInput = styled(NumericInput)({
  "& .numeric-input-view input": {
    padding: "14px",
    textAlign: "center",
    width: "100%",
    minWidth: "90px",
    // Beat the rtl right alignment
    ...onRtl({
      textAlign: "center",
    }),
  },
});

const Separator = styled("span")({
  fontSize: "14px",
  padding: "12px 5px",
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface BloodPressureQuestionProps extends QuestionsIF {}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const BloodPressureQuestion = ({
  onAnswer,
  miniView,
  ...props
}: BloodPressureQuestionProps) => {
  // Make sure the diastolic is shown first
  const suggestions = props.question?.suggestions.sort((a, b) =>
    a.suggestion.feature.id === NumericFeatureIds.SystolicBloodPressure
      ? -1
      : 1,
  );

  const initialAnswers = useMemo(
    () => Array(suggestions?.length || 0).fill(null),
    [suggestions],
  );

  const [answerValues, setAnswerValue] = useState(initialAnswers);

  const handleSubmit = useCallback(() => {
    onAnswer(answerValues);
  }, [answerValues, onAnswer]);

  const sendSkip = useCallback(() => {
    // Currently, we can skip only refinements (we can't negate the blood pressure)
    // So we need to open the qualified feature and mark it 'skip'
    const skippedQfs = suggestions.flatMap((s) => ({
      ...s.suggestion,
      skip: true,
    }));
    onAnswer(skippedQfs);
  }, [onAnswer, suggestions]);

  useEffect(() => {
    if (answerValues.some((a) => a) && miniView) {
      handleSubmit();
    }
  }, [answerValues, miniView, handleSubmit]);

  const valueHandler = useCallback(
    (value: QualifiedFeature, index) => {
      setAnswerValue((oldAnswer) => [
        ...oldAnswer.slice(0, index),
        value,
        ...oldAnswer.slice(index + 1),
      ]);
    },
    [setAnswerValue],
  );

  // If there is one null value, disable the submit.
  const disableSubmit = answerValues.some((i) => !i);

  const { translate } = useTranslate();

  /* Take the text from the first question :X */
  return (
    <BloodPressureQuestionView
      isAnswered={!!props.answer}
      onSubmitClick={handleSubmit}
      onSkipClicked={sendSkip}
      submitDisabled={disableSubmit}
      questionText={props.question?.question?.text}
      miniView={miniView}
    >
      <BloodPressureContainer
        className={clsx(
          "blood-pressure-container",
          miniView ? "mini-view" : "",
        )}
      >
        {miniView ? (
          <MiniViewText>
            {translate(
              "FORM_BLOOD_PRESSURE_TEXT",
            ) /*Since blood pressure don't have proper short text we use translate*/}
          </MiniViewText>
        ) : (
          <BloodPressureIcon />
        )}
        <InputsSection className={"input-section"}>
          {suggestions.map((suggestion, index) => (
            <>
              <BloodPressureInput
                className={"blood-pressure-input"}
                suggestion={suggestion}
                index={index}
                onValidValue={valueHandler}
              />
              {index < suggestions.length - 1 && <Separator>/</Separator>}
            </>
          ))}
        </InputsSection>
      </BloodPressureContainer>
    </BloodPressureQuestionView>
  );
};
